// QR Kiosk v2 — KV2-6 pipeline hook. Ports the v1 kiosk-chat send pipeline
// verbatim (create-thread-on-open non-fatal; optimistic user msg → streamMessage
// → readSSEStream → sync fallback) with the three v2 upgrades: (1) per-turn
// AbortController signal, (2) thread-id reconciliation via onThreadCreated/
// onThreadId, (3) stop() that commits the partial. Session lives in Redux
// (kioskV2Slice.midasSession) so it survives close/reopen; transient stream state
// stays local. UUID-guarded before thread-create AND every send (SEC-REQ-5,
// double enforcement with the dock). §3-T2.
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import chatBotApi from "../../../shared/chatBot/ChatBotApi";
import { readSSEStream, mapBotSources, mapBotArtifacts, mapBotActions } from "../../../shared/chatBot/streamBotMessage";
import { ChatMessage } from "../../../shared/chatBot/models/chatBotModels";
import { Machine } from "../../models/machinesModels";
import { isValidMachineUuid } from "../dashboard/kioskLiveStatus";
import {
  midasSessionStart,
  midasSessionSetThread,
  midasSessionAppend,
  midasSessionAttachDraft,
  KioskMidasSession,
} from "../kioskV2Slice";
import { mapDraftFromSse, KioskDraftTicket } from "./draftTicketContract";

const EMPTY_MESSAGES: ChatMessage[] = [];
const EMPTY_DRAFTS: Record<string, KioskDraftTicket> = {};
const EMPTY_CREATED: Record<string, string> = {};

const generateId = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const assistantMessage = (text: string): ChatMessage => ({
  id: generateId(),
  role: "assistant",
  text,
  timestamp: new Date().toISOString(),
});

export interface KioskMidasSessionApi {
  messages: ChatMessage[];
  threadId: string | null;
  isLoading: boolean;
  streamingText: string;
  currentStep: string | null;
  error: string | null;
  drafts: Record<string, KioskDraftTicket>;
  createdDrafts: Record<string, string>;
  send: (text: string, image?: File) => Promise<void>;
  stop: () => void;
}

export function useKioskMidasSession(machine: Machine): KioskMidasSessionApi {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const session = useAppSelector((s) => s.kioskV2.midasSession);
  const ready = isValidMachineUuid(machine.id);

  // Only trust the session once it is bound to THIS machine (guards the one frame
  // before midasSessionStart runs, and any stale other-machine session).
  const bound: KioskMidasSession | null = session && session.machineId === machine.id ? session : null;
  const messages = bound?.messages ?? EMPTY_MESSAGES;
  const threadId = bound?.threadId ?? null;
  const drafts = bound?.drafts ?? EMPTY_DRAFTS;
  const createdDrafts = bound?.createdDrafts ?? EMPTY_CREATED;

  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const controllerRef = useRef<AbortController | null>(null);
  const abortedByUserRef = useRef(false);
  const streamingTextRef = useRef("");
  const threadCreateAttempted = useRef<string | null>(null);

  // 1 · Session start / restore (mount + machine switch).
  useEffect(() => {
    if (ready) dispatch(midasSessionStart(machine.id));
  }, [machine.id, ready, dispatch]);

  // 2 · Thread create — only when this machine's session has NO thread and NO
  // messages (restore path skips it). Failure is non-fatal (chat works threadless,
  // v1 parity). No server-history fetch on restore (recorded non-goal §9).
  useEffect(() => {
    if (!ready || !bound) return;
    if (bound.threadId || bound.messages.length > 0) return;
    if (threadCreateAttempted.current === machine.id) return;
    threadCreateAttempted.current = machine.id;
    let cancelled = false;
    chatBotApi
      .createThread(machine.id)
      .then((t) => {
        if (!cancelled) dispatch(midasSessionSetThread(t.id));
      })
      .catch(() => {
        /* non-fatal — do NOT set error (v1 parity) */
      });
    return () => {
      cancelled = true;
    };
  }, [ready, bound, machine.id, dispatch]);

  const clearTransient = useCallback(() => {
    setIsLoading(false);
    setCurrentStep(null);
    setStreamingText("");
    streamingTextRef.current = "";
    controllerRef.current = null;
  }, []);

  const stop = useCallback(() => {
    abortedByUserRef.current = true;
    controllerRef.current?.abort();
    const partial = streamingTextRef.current;
    if (partial && partial.trim()) {
      dispatch(midasSessionAppend(assistantMessage(partial))); // keep what the user already read
    }
    setError(null); // user-initiated abort must not surface an error banner
    clearTransient();
  }, [dispatch, clearTransient]);

  // 5 · Unmount mid-stream (user pressed ‹ while streaming) → same stop semantics.
  useEffect(() => {
    return () => {
      if (!controllerRef.current) return;
      abortedByUserRef.current = true;
      controllerRef.current.abort();
      const partial = streamingTextRef.current;
      if (partial && partial.trim()) {
        dispatch(midasSessionAppend(assistantMessage(partial)));
      }
    };
  }, [dispatch]);

  const send = useCallback(
    async (text: string, image?: File) => {
      if (!ready) return;
      const trimmed = (text ?? "").trim();
      if (!trimmed && !image) return; // nothing to send — don't hit the API

      // Optimistic user message (objectURL preview + name when image).
      const userMsg: ChatMessage = {
        id: generateId(),
        role: "user",
        text: trimmed,
        timestamp: new Date().toISOString(),
        imageUrl: image ? URL.createObjectURL(image) : undefined,
        imageName: image?.name,
      };
      dispatch(midasSessionAppend(userMsg));

      setIsLoading(true);
      setCurrentStep(null);
      setStreamingText("");
      streamingTextRef.current = "";
      setError(null);

      const controller = new AbortController();
      controllerRef.current = controller;
      abortedByUserRef.current = false;
      const currentThreadId = threadId;

      let pendingDraft: KioskDraftTicket | null = null;
      let answerMessageId: string | null = null;

      const runSyncFallback = async () => {
        try {
          const syncRes = await chatBotApi.sendMessage(trimmed, machine.id, currentThreadId ?? undefined, image);
          dispatch(
            midasSessionAppend({
              id: generateId(),
              role: "assistant",
              text: syncRes.message,
              timestamp: new Date().toISOString(),
              sources: mapBotSources(syncRes.sources),
              suggestions: syncRes.suggestions,
              intent: syncRes.metadata?.intent,
              artifacts: mapBotArtifacts((syncRes as any).artifacts),
              actions: mapBotActions((syncRes as any).actions),
            })
          );
        } catch {
          if (!abortedByUserRef.current) setError(translate("kioskV2MidasSendFailed"));
        }
      };

      let response: Response | null = null;
      try {
        response = await chatBotApi.streamMessage(
          trimmed,
          machine.id,
          currentThreadId ?? undefined,
          image,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          controller.signal
        );
      } catch {
        if (abortedByUserRef.current) {
          clearTransient();
          return;
        }
        await runSyncFallback();
        clearTransient();
        return;
      }

      if (!response) {
        await runSyncFallback();
        clearTransient();
        return;
      }

      let handled = false;
      let streamErr: string | null = null;
      const botMessage = await readSSEStream(response, {
        onStep: (k) => setCurrentStep(k),
        onToken: (t) => {
          streamingTextRef.current += t;
          setStreamingText(streamingTextRef.current);
        },
        onMessage: (msg) => {
          if (handled) return;
          handled = true;
          answerMessageId = msg.id;
          dispatch(midasSessionAppend(msg));
          if (pendingDraft) dispatch(midasSessionAttachDraft({ messageId: msg.id, draft: pendingDraft }));
          setStreamingText("");
          streamingTextRef.current = "";
          setCurrentStep(null);
          setIsLoading(false);
        },
        onError: (m) => {
          streamErr = m;
          if (!abortedByUserRef.current) setError(translate("kioskV2MidasSendFailed"));
          setStreamingText("");
          streamingTextRef.current = "";
        },
        onThreadCreated: (tid) => {
          if (!currentThreadId) dispatch(midasSessionSetThread(tid));
        },
        onThreadId: (tid) => {
          if (!currentThreadId) dispatch(midasSessionSetThread(tid));
        },
        onActionRequest: (req) => {
          const d = mapDraftFromSse({ kind: "action", actionRequest: req }, machine.id); // null while flag off
          if (!d) return;
          if (answerMessageId) dispatch(midasSessionAttachDraft({ messageId: answerMessageId, draft: d }));
          else pendingDraft = d;
        },
      });

      if (abortedByUserRef.current) {
        clearTransient();
        return;
      }
      if (!handled && botMessage) {
        dispatch(midasSessionAppend(botMessage));
        if (pendingDraft) dispatch(midasSessionAttachDraft({ messageId: botMessage.id, draft: pendingDraft }));
      } else if (!handled && !streamErr) {
        await runSyncFallback(); // stream ended with no answer + no error
      }
      clearTransient();
    },
    [ready, threadId, machine.id, dispatch, translate, clearTransient]
  );

  return { messages, threadId, isLoading, streamingText, currentStep, error, drafts, createdDrafts, send, stop };
}
